import { TimeEntry } from '/@/modules/timeentry/models/TimeEntry';
import { ModelCollection } from '/@/modules/core/app/models/ModelCollection';
import { isOnCanonicalDate, minuteDurationToTime } from '/@/modules/core/app/helpers/DateHelper';
import { TimeEntryType } from '@/modules/timeentry/enums/TimeEntryType';
import { TimeEntryTypeKey } from '@/modules/timeentry/constants/TimeEntryTypeKey';
import { User } from '@/modules/user/models/User';
import { uniqBy } from 'lodash';

export class TimeEntryCollection extends ModelCollection<TimeEntry> {
  constructor(items: TimeEntry[] | TimeEntryCollection | null = null) {
    super(items, TimeEntry);
  }

  static mapModel(items: TimeEntry[] | TimeEntryCollection | null = null): TimeEntryCollection {
    return new this(items);
  }

  get totalDuration(): number | undefined {
    return this.items?.reduce((sum: number, item: TimeEntry) => sum + (item.duration ?? 0), 0);
  }

  get totalDurationFormatted(): string {
    return minuteDurationToTime(this.totalDuration);
  }

  get entryTypes(): TimeEntryTypeKey[] {
    return [...new Set(this.items?.map((item: TimeEntry) => item.type))] as TimeEntryTypeKey[];
  }

  get entryEstateNames(): string[] {
    return [...new Set(this.items?.map((item: TimeEntry) => item.estate?.fullName))].filter(
      Boolean
    ) as string[];
  }

  get groupedEntriesByType(): Record<TimeEntryTypeKey | string, TimeEntryCollection> {
    return this.entryTypes.reduce(
      (
        acc: Record<TimeEntryTypeKey | string, TimeEntryCollection>,
        type: TimeEntryTypeKey | undefined
      ) => {
        if (TimeEntryType.isEstateWork(type as TimeEntryTypeKey)) {
          acc = { ...acc, ...this.groupedEntriesByEstateNames };
        } else {
          acc[type as TimeEntryTypeKey] = TimeEntryCollection.mapModel(
            this.getEntriesByType(type as TimeEntryTypeKey)
          );
        }

        return acc;
      },
      {}
    );
  }

  get groupedEntriesByEstateNames(): Record<string, TimeEntryCollection> {
    return this.entryEstateNames.reduce(
      (acc: Record<string, TimeEntryCollection>, estateName: string) => {
        acc[estateName] = TimeEntryCollection.mapModel(this.getEntriesByEstateName(estateName));

        return acc;
      },
      {}
    );
  }

  getEntriesForWeek(weekDates: string[]): TimeEntryCollection[] {
    return weekDates.map((date: string) => this.getEntriesForDate(date));
  }

  get usersWithTimeEntries(): User[] {
    const users: User[] = uniqBy(
      this.items?.map((timeEntry: TimeEntry) => timeEntry.user as User),
      'id'
    );

    users.forEach((user: User) => {
      user.timeEntries = TimeEntryCollection.mapModel(
        this.items?.filter((timeEntry: TimeEntry) => timeEntry.userId === user?.id)
      );
    });

    return users;
  }

  private getEntriesByType(type: TimeEntryTypeKey): TimeEntry[] {
    return this.items?.filter((entry: TimeEntry) => entry.type === type) ?? [];
  }

  private getEntriesByEstateName(estateName: string): TimeEntry[] {
    return this.items?.filter((entry: TimeEntry) => entry.estate?.fullName === estateName) ?? [];
  }

  private getEntriesForDate(date: string): TimeEntryCollection {
    return TimeEntryCollection.mapModel(
      this.items?.filter((entry: TimeEntry) => isOnCanonicalDate(entry.startedAt, date))
    );
  }

  updateByTempId(timeEntry: TimeEntry): void {
    const existingEntry: TimeEntry | undefined = this.findByKey(timeEntry.tempId, 'tempId');
    if (existingEntry) {
      this.update(timeEntry, 'tempId');
    }
  }
}
